import { supertypeClass, property } from '@haventech/amorphic';
import { CashFlow } from './CashFlow';
import { Applicant } from '../customer/Applicant';

@supertypeClass({ toClient: true, toServer: true })
export class EarnedIncome extends CashFlow {
  @property()
  taxRate: number = 0.33;

  @property({ getType: () => EarnedIncome })
  earnedIncome: EarnedIncome;

  adjustFlow(flow) {
    flow.amount += flow.amount * this.customer.settings.inflation;
  }

  @property({ rule: ['currency'], validate: 'isWithin(0, 10000000)' })
  amount: number;

  @property({ rule: ['currency'], validate: 'isWithin(0, 10000000)' })
  potential: number;

  @property({
    values: ['none', 'work', 'same'],
    descriptions: { none: 'Not work at all', work: 'Earn a different amount ...', same: 'Earn the same amount' },
  })
  incomeOption: string = 'none';

  @property({
    values: ['true', 'false'],
    descriptions: function () {
      let desc = { true: 'Yes', false: 'No' };
      //Shanks - Subclass this for Haven
      if (this.customer && this.customer.constructor && this.customer.constructor.name === 'HavenCustomer') {
        desc = { true: 'They work fulltime', false: "They don't work fulltime" };
      }
      return desc;
    },
  })
  works: boolean;

  @property({ rule: ['currency'], validate: 'isWithin(0, 10000000)' })
  deceasedAmount: number;

  @property()
  increase: number;

  @property()
  yearsWorked: number = 1;

  @property()
  applicant: Applicant;

  @property({ values: { false: 'will need to buy health insurance', true: "won't need to buy health insurance" } })
  hasHealthCare: boolean;

  @property({ values: { false: 'Not need money for childcare', true: 'Need money for childcare' } })
  needsChildCare: boolean;

  @property({ isLocal: true })
  hasNoIncome: boolean = false;

  constructor() {
    super();
  }

  static new(customer?, applicant?, amount?) {
    return new EarnedIncome().init(customer, applicant, amount);
  }

  init(customer?, applicant?, amount?) {
    super.init(customer, amount, 'earnings');

    this.applicant = applicant;
    this.ageTrigger();
    this.potentialTrigger();
    this.hasNoIncome = false;
    return this;
  }

  reset() {
    this.amount = 0;
    this.potential = 0;
    this.incomeOption = 'none';
    this.works = false;
    this.hasHealthCare = false;
    this.needsChildCare = false;
  }

  isActive(deceased?) {
    return this.inCashFlow(deceased);
  }

  inCashFlow(deceased?) {
    return this.getIncome(deceased) > 0;
  }

  ageTrigger() {
    const age = Number(this.applicant.person.age) || 0;
    this.yearsWorked = Math.max(1, age - 25);
  }

  hasNoIncomeTrigger() {
    if (this.hasNoIncome === true) {
      this.amount = 0;
    }
  }

  worksTrigger() {
    if (this.works) {
      this.needsChildCare = true;
      this.hasHealthCare = true;
    } else {
      this.needsChildCare = false;
      this.hasHealthCare = false;
    }
  }

  deceasedAmountTrigger() {
    const controller = (window as any).controller;
    if ((!controller.previous_value && controller.value) || (controller.previous_value && !controller.value)) {
      this.hasHealthCare = this.deceasedAmount > this.customer.assumptions.healthCareSalaryThreshold;
    }
  }

  incomeOptionTrigger() {
    this.hasHealthCare = this.amount > this.customer.assumptions.healthCareSalaryThreshold;
    this.needsChildCare = this.incomeOption !== 'none';
  }

  defaultIncomeOptions() {
    if (this.amount > 0) {
      if (this.incomeOption !== 'work') {
        this.incomeOption = 'same';
      }
    } else {
      this.incomeOption = 'none';
    }

    this.deceasedAmount = this.amount;
  }

  defaultCaregiver() {
    // Tobe obsolete
    if (this.customer.capitalNeeds.children.length === 0) {
      return;
    }

    const values = {
      '00': 'both',
      '10': 'alternate',
      '01': 'primary',
      '11': 'other',
    };

    let matrix;

    if (this.customer.profile.relationship === 'single') {
      matrix = (this.customer.capitalNeeds.earnedIncome[0].amount > 0 ? '1' : '0') + '1';
    } else {
      matrix =
        (this.customer.capitalNeeds.earnedIncome[0].amount > 0 ? '1' : '0') +
        (this.customer.capitalNeeds.earnedIncome[1].amount > 0 ? '1' : '0');
    }

    this.customer.profile.childCareGiver = values[matrix];
  }

  potentialTrigger() {
    if (this.amount > 0) {
      this.increase = this.getSalaryIncrease(this.amount, this.potential, 10);
    }
  }

  getStartingFlow(deceased) {
    const flow = super.getStartingFlow(deceased);

    flow.amount = this.getIncome();

    return flow;
  }

  getIncome(deceased?) {
    if (deceased === this.applicant) {
      return 0;
    }

    if (this.incomeOption === 'same') {
      return this.amount;
    } else if (this.incomeOption === 'none') {
      return 0;
    } else {
      return this.deceasedAmount;
    }
  }

  getAmount(flow, year?, _deceased?, yearOfDeath?) {
    return year < yearOfDeath || this.applicant.person.age + year < this.customer.settings.retirementAge
      ? flow.amount * (1 - this.taxRate)
      : 0;
  }

  getDescription() {
    return this.applicant === this.customer.primaryCustomer ? 'Your income' : 'Spouse Income';
  }

  getSalaryIncrease(presentAmount, futureAmount, years) {
    return Math.pow(futureAmount / presentAmount, 1 / years) - 1;
  }

  getSalaryPotential(presentAmount, increase, years) {
    let total = presentAmount;

    for (let ix = 0; ix <= years; ++ix) {
      total += total * increase;
    }

    return total;
  }
}
